// App.js
import React, { useEffect, useRef } from "react";
import { Routes, Route } from "react-router-dom";
import PrivateRoute from "./components/PrivateRoute";
import Login from "./pages/Login";
import SetupPassword from "./pages/SetupPassword";
import NotFound from "./pages/NotFound";
import RequestPasswordReset from "./components/RequestPasswordReset";
import SetNewPassword from "./components/SetNewPassword";
import ResetPassword from "./pages/ResetPassword";
import CreateMonthlyReportPage from "./components/reports/MonthlyReportForm";
import { ToastContainer } from "react-toastify";
import { useAuth } from "./contexts/AuthContext";
import CrmDashboard from "./pages/CrmDashboard";

// ✅ CRITICAL: Import and use the auth integration hook
import { useAuthIntegration } from "./hooks/useAuthIntegration";

// ✅ HIPAA: Import HIPAA-compliant storage initialization
import { initializeHIPAACompliantStorage } from "./utils/usePersistentState";

import "bootstrap-icons/font/bootstrap-icons.css";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/page.css";
import "./assets/css/chat.css";

import "./components/announcements/announcements.css";
import "./test-env.js";

const App = () => {
  // ✅ CRITICAL: Add the auth integration hook at the very top
  useAuthIntegration();

  const {
    // Updated to use new stable properties
    isAuthenticated,
    user,
    loading,
    isInitialized, // NEW: prevents premature renders
    // 🔧 REMOVED session timeout functions - AuthContext handles everything
  } = useAuth();

  // 🔧 KEEP: Your scroll position preservation
  const scrollPositionRef = useRef(0);

  // 🔧 KEEP: Your scroll handling logic
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ KEEP: Your HIPAA storage initialization
  useEffect(() => {
    console.log("🔐 Initializing HIPAA-compliant storage...");

    try {
      initializeHIPAACompliantStorage();
      console.log("✅ HIPAA storage initialized successfully");
    } catch (error) {
      console.error("❌ Failed to initialize HIPAA storage:", error);
    }
  }, []);

  // ✅ UPDATED: Better loading state check using isInitialized
  // This prevents showing login page before AuthContext is ready
  if (!isInitialized || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">🔄 Loading CRP application...</p>
          <small className="text-muted">
            🔐 Verifying HIPAA-compliant session...
          </small>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ❌ REMOVED <Router> wrapper - BrowserRouter is already in index.js */}
      {/* ✅ ONLY Routes and Route components - no nested Router */}
      <Routes>
        {/* 🌐 KEEP: Your public routes structure */}
        <Route path="/login" element={<Login />} />
        <Route path="/setup-password/:user_id" element={<SetupPassword />} />
        <Route path="/reset-password" element={<RequestPasswordReset />} />
        <Route path="/set-password/:user_id" element={<SetNewPassword />} />
        <Route path="/reset-password/:user_id" element={<ResetPassword />} />

        {/* 🔒 KEEP: Your protected routes */}
        <Route
          path="/crm/*"
          element={
            <PrivateRoute>
              <CrmDashboard />
            </PrivateRoute>
          }
        />

        {/* KEEP: Your monthly report shortcut */}
        <Route
          path="/monthly-reports/create"
          element={
            <PrivateRoute>
              <CreateMonthlyReportPage />
            </PrivateRoute>
          }
        />

        {/* 🏠 UPDATED: Root route logic */}
        <Route
          path="/"
          element={
            isAuthenticated && user ? (
              <PrivateRoute>
                <CrmDashboard />
              </PrivateRoute>
            ) : (
              <Login />
            )
          }
        />

        {/* 🚧 KEEP: Your 404 handler */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* 🔧 REMOVED: Session timeout modal - AuthContext handles this internally */}
      {/* The SessionTimeoutWarning is now rendered inside the refactored AuthContext */}

      {/* ✅ KEEP: Your toast configuration with HIPAA compliance */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        // ✅ KEEP: HIPAA-compliant styling
        toastClassName="hipaa-compliant-toast"
      />
    </>
  );
};

export default App;
